import { useState } from 'react';
import { Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { AdminTenant, AdminTenantListFilters } from '@/api/models';
import { FiltersPopover } from '@/components/shell/FiltersPopover';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { useCanWrite } from '@/access/useCanWrite';
import { countActiveTenantFilters } from './filters/activeFilterCount';
import { TenantsFiltersForm } from './filters/TenantsFiltersForm';
import { TenantsListView } from './TenantsListView';
import { TenantCreateDialog } from './TenantCreateDialog';

const DEFAULT_FILTERS: AdminTenantListFilters = { page: 1, pageSize: 50 };
const PAGINATION_ONLY_KEYS = new Set(['page', 'pageSize']);

/**
 * Tenants surface against `/v1/admin/tenants` (A2's contract). List with
 * the contract's filters/pagination and a create action; `platform:auditor`
 * sees the same list read-only — the Create button is hidden via
 * `useCanWrite`, the UI-level convenience, while every mutating call still
 * relies on the API's own `403 access_denied` as the real boundary.
 */
export function TenantsPage() {
  const translate = useTranslate();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [filters, setFilters] = useState<AdminTenantListFilters>(DEFAULT_FILTERS);
  const [refreshToken, setRefreshToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  function updateFilters(patch: Partial<AdminTenantListFilters>) {
    const isPaginationOnly = Object.keys(patch).every((key) => PAGINATION_ONLY_KEYS.has(key));
    setFilters((prev) => ({ ...prev, ...patch, page: isPaginationOnly ? (patch.page ?? prev.page) : 1 }));
  }

  function handleCreated(tenant: AdminTenant) {
    setCreateOpen(false);
    setRefreshToken((token) => token + 1);
    navigate(`/tenants/${encodeURIComponent(tenant.code)}`);
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        left={<PageTitle title={translate('pay.tenants.pageTitle')} subtitle={translate('pay.tenants.pageSubtitle')} />}
        filters={
          <FiltersPopover activeCount={countActiveTenantFilters(filters)} onClear={() => setFilters(DEFAULT_FILTERS)}>
            <TenantsFiltersForm filters={filters} onChange={updateFilters} />
          </FiltersPopover>
        }
        onRefresh={() => setRefreshToken((token) => token + 1)}
        onCreate={canWrite ? () => setCreateOpen(true) : undefined}
        createLabel={translate('pay.tenants.list.createLabel')}
      />
      <TenantsListView key={refreshToken} filters={filters} onFiltersChange={updateFilters} />
      <TenantCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
    </Stack>
  );
}
